/**
 * Subcomponentes visuales para Análisis de Ventas.
 * Estilo unificado con "Dashboard de Lecciones Aprendidas": paleta mate,
 * headers beige, ícono bronce, título Playfair. Fondo página #F5F5F5.
 */
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ComposedChart, Line, Cell, ReferenceLine,
} from 'recharts';
import {
  Zap, BarChart3, Bot, ClipboardList, TrendingUp, Building2, Trophy, Grid3x3,
  Sliders, Wallet, LineChart, Edit2, X as XIcon,
} from 'lucide-react';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';

// Paleta mate (misma que Dashboard de Lecciones Aprendidas)
export const PALETTE = ['#9d8272', '#7a9e5f', '#c0855a', '#b85555', '#c5b8a2', '#8a7162'];
// Colores neutros de UI/ejes alineados al skin activo (leídos en runtime desde CSS vars)
const readVar = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};
export const COLOR_STRONG = readVar('--skin-ink', '#1A1A1E');
export const COLOR_MED = readVar('--skin-ink-soft', '#6E6E78');
export const COLOR_SOFT = readVar('--skin-ink-mute', '#A79C86');
export const COLOR_GRID = readVar('--skin-line-soft', '#EDEAE2');
export const COLOR_BRONCE = '#9d8272';
export const COLOR_BRONCE_DARK = '#5a4a3a';
export const COLOR_VERDE = '#7a9e5f';
export const COLOR_ROJO = '#b85555';
export const COLOR_NARANJA = '#c0855a';

// ----------------------------- Utilidades -----------------------------

const fmtInt = (n) =>
  typeof n === 'number' ? n.toLocaleString('es-PE', { maximumFractionDigits: 0 }) : (n == null ? '—' : n);
const fmtDec = (n, d = 1) =>
  typeof n === 'number' ? n.toLocaleString('es-PE', { maximumFractionDigits: d }) : (n == null ? '—' : n);
const fmtPct = (n) => (n == null ? '—' : `${(n * 100).toFixed(1)}%`);
const fmtSoles = (n) => (typeof n === 'number' ? `S/. ${fmtInt(n)}` : (n == null ? '—' : n));

// Convierte 'yyyy-MM' a texto humano en español, ej: '2026-01' → 'ene 2026'.
const MESES_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun',
                  'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const fmtAnioMes = (am) => {
  if (!am || typeof am !== 'string' || am.length < 7) return '';
  const y = parseInt(am.slice(0, 4), 10);
  const m = parseInt(am.slice(5, 7), 10);
  if (!y || !m || m < 1 || m > 12) return am;
  return `${MESES_ES[m - 1]} ${y}`;
};

// Badge "Proyecto activo desde ene 2026 · 2 meses" — visible solo cuando la
// antigüedad del proyecto es menor a 12 meses (el divisor de las ventanas
// temporales queda ajustado a la realidad).
export const ProyectoJovenBadge = ({ mesesOperativos, primeraVentaAm }) => {
  if (!mesesOperativos || mesesOperativos <= 0 || mesesOperativos >= 12) return null;
  const desde = fmtAnioMes(primeraVentaAm);
  const suffix = `${mesesOperativos} mes${mesesOperativos === 1 ? '' : 'es'}`;
  return (
    <div
      className="text-[11px] mb-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md"
      style={{
        background: 'var(--skin-line, #f5f1ea)',
        color: 'var(--skin-accent-dark, #7A5C3E)',
        border: '1px dashed var(--skin-accent, #b7986e)',
      }}
    >
      <span>Proyecto activo desde <b>{desde}</b> · <b>{suffix}</b></span>
    </div>
  );
};

// --------------------------- Card wrapper ------------------------------

export const Card = ({ icon, title, subtitle, action, children, className = '' }) => (
  <div className={`skin-panel ${className}`}>
    <div className="skin-panel-head" style={{ gap: '8px' }}>
      {icon && <span style={{ color: 'var(--skin-accent)' }} className="flex-shrink-0 inline-flex">{icon}</span>}
      <h3 className="flex-1 min-w-0 sm:whitespace-nowrap sm:overflow-hidden sm:text-ellipsis" style={{ margin: 0, fontSize: '13.5px' }}>
        {title}
      </h3>
      {subtitle && (
        <span className="skin-count flex-shrink-0 sm:whitespace-nowrap truncate max-w-[45%]">
          {subtitle}
        </span>
      )}
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// ------------------------------- KPIs ---------------------------------

export const KpiCard = ({ avanceFiltro, mediaProyecto }) => (
  <div
    className="skin-panel p-4 flex flex-col items-center justify-center h-full"
    style={{ borderTop: '3px solid var(--skin-accent)' }}
    data-testid="av-kpi-avance"
  >
    <p className="skin-field-label text-center" style={{ marginBottom: '4px' }}>
      Avance Filtro
    </p>
    <p
      className="text-4xl font-bold text-center"
      style={{ fontFamily: 'var(--skin-font-display)', color: 'var(--skin-accent-dark)' }}
    >
      {fmtPct(avanceFiltro)}
    </p>
    <p className="text-[11px] skin-text-mute mt-2">
      Media Proyecto: {fmtPct(mediaProyecto)}
    </p>
  </div>
);

// ------------------------ Matriz de Velocidades -----------------------

export const MatrizVelocidadesCard = ({ kpis, rows, mesesOperativos, primeraVentaAm }) => (
  <Card
    icon={<Zap size={14} />}
    title="Matriz de Velocidades y Precios (filtro actual)"
    subtitle={
      <span className="inline-block bg-[#5a4a3a] text-[#fefaf3] rounded-md px-2 py-0.5">
        Total {fmtInt(kpis?.total_filtro)} · Ventas {fmtInt(kpis?.ventas)} · Stock {fmtInt(kpis?.stock)}
      </span>
    }
    className="h-full flex flex-col"
  >
    <div data-testid="av-matriz-velocidades" className="overflow-x-auto -mx-4 -mt-4 px-4 pt-2">
      <ProyectoJovenBadge mesesOperativos={mesesOperativos} primeraVentaAm={primeraVentaAm} />
      <TooltipProvider delayDuration={150}>
        <table className="w-full text-sm min-w-[520px]">
          <thead>
            <tr className="text-[#888888] text-[10px] uppercase tracking-wider">
              <th className="text-left px-2 py-2 font-medium border-b border-[#e8dfd0]">Ventana Temporal</th>
              <th className="text-right px-2 py-2 font-medium border-b border-[#e8dfd0]">Velocidad (Und/Mes)</th>
              <th className="text-right px-2 py-2 font-medium border-b border-[#e8dfd0]">Volumen (M²/Mes)</th>
              <th className="text-right px-2 py-2 font-medium border-b border-[#e8dfd0]">M.A.S (Stk/Vel)</th>
              <th className="text-right px-2 py-2 font-medium border-b border-[#e8dfd0]">Valor Cierre (S/./M²)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const highlight = i === 0 || r.ventana.startsWith('Histórico');
              const masVal = r.meses_acabar_stock;
              const hasMas = masVal !== null && masVal !== undefined;
              return (
                <tr
                  key={r.ventana}
                  className={`border-b border-[#f5f1ea] ${
                    highlight ? 'bg-[#fdf8ee] font-semibold' : ''
                  }`}
                  data-testid={`av-velocidad-row-${i}`}
                >
                  <td className="px-2 py-2 text-[#2D2D2D]">{r.ventana}</td>
                  <td className="px-2 py-2 text-right text-[#2D2D2D]">
                    {fmtDec(r.velocidad_und_mes)}
                  </td>
                  <td className="px-2 py-2 text-right text-[#2D2D2D]">
                    {fmtInt(r.volumen_m2_mes)}
                  </td>
                  <td
                    className="px-2 py-2 text-right text-[#2D2D2D]"
                    data-testid={`av-mas-${i}`}
                  >
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <span className={hasMas ? 'cursor-help underline decoration-dotted underline-offset-2' : 'skin-text-mute'}>
                          {hasMas ? fmtDec(masVal) : '—'}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        <div className="font-semibold mb-1">M.A.S = Stock disponible / Velocidad promedio</div>
                        <div className="border-t border-white/20 pt-1 space-y-0.5">
                          <div>Stock disponible: <b>{fmtInt(r.stock_referencia_und)} unidades</b></div>
                          <div>Velocidad ({r.ventana}): <b>{fmtDec(r.velocidad_und_mes)} u/mes</b></div>
                        </div>
                        <div className="border-t border-white/20 pt-1 mt-1">
                          {hasMas
                            ? <>Resultado: <b>{fmtDec(masVal)} meses</b></>
                            : <span className="italic">Velocidad = 0 en esta ventana, no se puede calcular M.A.S.</span>
                          }
                        </div>
                      </TooltipContent>
                    </UITooltip>
                  </td>
                  <td className="px-2 py-2 text-right font-semibold" style={{ color: COLOR_VERDE }}>
                    {fmtSoles(r.valor_cierre_soles_m2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TooltipProvider>
    </div>
  </Card>
);

// -------------------------- Matriz de MIX -----------------------------

export const MatrizMixCard = ({ rows }) => (
  <Card
    icon={<BarChart3 size={14} />}
    title="Matriz de Distribución del Mix Comercial: Original vs Real Vendido"
  >
    <div className="overflow-x-auto" data-testid="av-matriz-mix">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[#888888] text-[10px] uppercase tracking-wider bg-[#faf7f2]">
            <th className="text-left px-1.5 py-2 font-medium" rowSpan={2}>Tipología</th>
            <th className="text-right px-1.5 py-2 font-medium" rowSpan={2}>Área (m²)</th>
            <th className="text-center px-1.5 py-1 font-medium border-l border-[#e8dfd0] whitespace-normal leading-tight" colSpan={2}>Stock Inicial Universo</th>
            <th className="text-center px-1.5 py-1 font-medium border-l border-[#e8dfd0] whitespace-normal leading-tight" colSpan={2}>Unidades Vendidas</th>
          </tr>
          <tr className="text-[#888888] text-[10px] uppercase tracking-wider">
            <th className="text-right px-1.5 py-1 font-medium border-l border-[#e8dfd0]">Und</th>
            <th className="text-right px-1.5 py-1 font-medium">% Mix Original</th>
            <th className="text-right px-1.5 py-1 font-medium border-l border-[#e8dfd0]">Und</th>
            <th className="text-right px-1.5 py-1 font-medium">% Mix Ventas</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const outperforms = r.mix_ventas_pct >= r.mix_original_pct;
            return (
              <tr
                key={r.tipologia + i}
                className="border-b border-[#f5f1ea]"
                data-testid={`av-mix-row-${r.tipologia}`}
              >
                <td className="px-1.5 py-2 text-[#2D2D2D] font-medium">{r.tipologia}</td>
                <td className="px-1.5 py-2 text-right text-[#2D2D2D]">{fmtDec(r.area_m2, 2)}</td>
                <td className="px-1.5 py-2 text-right border-l border-[#e8dfd0] font-semibold" style={{ color: COLOR_BRONCE }}>
                  {fmtInt(r.stock_inicial_und)}
                </td>
                <td className="px-1.5 py-2 text-right text-[#5A5A5A]">
                  {fmtPct(r.mix_original_pct)}
                </td>
                <td className="px-1.5 py-2 text-right border-l border-[#e8dfd0] font-semibold" style={{ color: COLOR_VERDE }}>
                  {fmtInt(r.vendidas_und)}
                </td>
                <td className="px-1.5 py-2 text-right">
                  <span
                    className="inline-block text-white px-2 py-0.5 rounded font-semibold"
                    style={{ background: outperforms ? COLOR_VERDE : COLOR_ROJO }}
                  >
                    {fmtPct(r.mix_ventas_pct)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </Card>
);

// ----------------- Matriz de Distribución Venta Estacionamientos --------

export const MatrizEstacionamientosCard = ({ rows, mesesOperativos, primeraVentaAm }) => (
  <Card
    icon={<Building2 size={14} />}
    title="Matriz de Distribución Venta Estacionamientos"
  >
    <div className="overflow-x-auto" data-testid="av-matriz-estacionamientos">
      <ProyectoJovenBadge mesesOperativos={mesesOperativos} primeraVentaAm={primeraVentaAm} />
      <table className="w-full text-sm min-w-[380px]">
        <thead>
          <tr className="text-[#888888] text-[10px] uppercase tracking-wider bg-[#faf7f2]">
            <th className="text-left px-3 py-2 font-medium border-b border-[#e8dfd0]">Ventana Temporal</th>
            <th className="text-right px-3 py-2 font-medium border-b border-[#e8dfd0]"># Estac Vendidos</th>
            <th className="text-right px-3 py-2 font-medium border-b border-[#e8dfd0]"># Depa Vendidos</th>
            <th className="text-right px-3 py-2 font-medium border-b border-[#e8dfd0]">% Mix Original</th>
            <th className="text-right px-3 py-2 font-medium border-b border-[#e8dfd0]">% Mix Ventas</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => {
            const orig = r.mix_original_pct;
            const vent = r.mix_ventas_pct;
            const hasBoth = orig != null && vent != null;
            const outperforms = hasBoth && vent >= orig;
            return (
              <tr
                key={r.ventana}
                className="border-b border-[#f5f1ea]"
                data-testid={`av-estac-row-${i}`}
              >
                <td className="px-3 py-2 text-[#2D2D2D] font-medium whitespace-nowrap">{r.ventana}</td>
                <td className="px-3 py-2 text-right font-semibold" style={{ color: COLOR_BRONCE }}>
                  {fmtInt(r.estac_vendidos)}
                </td>
                <td className="px-3 py-2 text-right text-[#2D2D2D]">
                  {fmtInt(r.depa_vendidos)}
                </td>
                <td className="px-3 py-2 text-right text-[#5A5A5A]">
                  {fmtPct(orig)}
                </td>
                <td className="px-3 py-2 text-right">
                  {vent == null ? (
                    <span className="skin-text-mute">—</span>
                  ) : (
                    <span
                      className="inline-block text-white px-2 py-0.5 rounded font-semibold"
                      style={{ background: outperforms ? COLOR_VERDE : COLOR_ROJO }}
                    >
                      {fmtPct(vent)}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </Card>
);

export const EvolucionChart = ({ data }) => (
  <Card
    icon={<TrendingUp size={14} />}
    title="Evolución Comercial: Facturación Mensual vs Valor por m² Real"
  >
    <div data-testid="av-evolucion-chart">
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
          <CartesianGrid stroke={COLOR_GRID} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: COLOR_MED }} interval="preserveStartEnd" />
          <YAxis yAxisId="left" tick={{ fontSize: 10, fill: COLOR_MED }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: COLOR_MED }}
                 domain={['auto', 'auto']} />
          <Tooltip
            formatter={(v) => fmtSoles(v)}
            contentStyle={{ background: '#fff', border: '1px solid #e8dfd0', borderRadius: 6, fontSize: 12 }}
          />
          <Bar yAxisId="left" dataKey="facturacion_soles" name="Facturación Mensual (S/.)"
               fill={COLOR_BRONCE} />
          <Line yAxisId="right" type="monotone" dataKey="valor_m2_real"
                name="S/. x m² Real" stroke={COLOR_NARANJA} strokeWidth={2}
                dot={{ r: 3, fill: COLOR_NARANJA }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

// -------------------- Colocación m² + Unidades ------------------------

export const ColocacionChart = ({ data }) => (
  <Card
    icon={<Building2 size={14} />}
    title="Colocación de Metraje Cuadrado y Volumen de Unidades"
  >
    <div data-testid="av-colocacion-chart">
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
          <CartesianGrid stroke={COLOR_GRID} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: COLOR_MED }} interval="preserveStartEnd" />
          <YAxis yAxisId="left" tick={{ fontSize: 10, fill: COLOR_MED }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: COLOR_MED }} />
          <Tooltip
            contentStyle={{ background: '#fff', border: '1px solid #e8dfd0', borderRadius: 6, fontSize: 12 }}
          />
          <Bar yAxisId="left" dataKey="m2_colocados" name="m² Colocados" fill="#8a7162" />
          <Line yAxisId="right" type="monotone" dataKey="unidades" name="Unidades (Und)"
                stroke={COLOR_VERDE} strokeWidth={2} dot={{ r: 3, fill: COLOR_VERDE }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

// Tooltip enriquecido para EficienciaChart
const EficienciaTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-[#e8dfd0] rounded shadow-md p-2 text-xs">
      <p className="font-semibold text-[#2D2D2D]">{d.tooltipTitle}</p>
      <p className="text-[#5A5A5A]">
        Absorción: <strong>{d.pct}%</strong>
      </p>
      <p className="text-[#5A5A5A]">Capacidad: {d.capacidad_und} u</p>
      <p className="text-[#5A5A5A]">S/. {fmtInt(d.precio_soles_m2)}/m²</p>
    </div>
  );
};

// ------------------ Eficiencia (barras horizontales) ------------------

export const EficienciaChart = ({ data, benchmark }) => {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 639px)').matches
      : false,
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const sorted = [...data].sort((a, b) => b.absorcion_pct - a.absorcion_pct);
  const rows = sorted.map((r) => ({
    ...r,
    label: `${r.tipologia} · ${r.columna}`,
    tooltipTitle: `${r.tipologia} (Línea ${r.columna})`,
    pct: Math.round(r.absorcion_pct * 100),
  }));

  const yWidth = isMobile ? 80 : 110;
  const leftMargin = isMobile ? 5 : 10;
  const fontSize = isMobile ? 9 : 10;

  return (
    <Card
      icon={<Trophy size={14} />}
      title="Eficiencia por Tipología frente a la Línea de Benchmark"
      subtitle={<span style={{ color: COLOR_ROJO }}>Benchmark: {fmtPct(benchmark)}</span>}
    >
      <div data-testid="av-eficiencia-chart">
        <ResponsiveContainer width="100%" height={Math.max(220, rows.length * 26)}>
          <BarChart
            data={rows}
            layout="vertical"
            margin={{ top: 5, right: 40, left: leftMargin, bottom: 5 }}
          >
            <CartesianGrid stroke={COLOR_GRID} />
            <XAxis
              type="number"
              domain={[0, 1]}
              tickFormatter={(v) => `${Math.round(v * 100)}%`}
              tick={{ fontSize, fill: COLOR_MED }}
            />
            <YAxis
              dataKey="label"
              type="category"
              tick={{ fontSize, fill: COLOR_MED }}
              width={yWidth}
            />
            <Tooltip content={<EficienciaTooltip />} />
            {benchmark != null && (
              <ReferenceLine
                x={benchmark}
                stroke={COLOR_ROJO}
                strokeDasharray="6 4"
                strokeWidth={2}
                ifOverflow="extendDomain"
                label={{
                  value: `Benchmark ${Math.round(benchmark * 100)}%`,
                  position: 'top',
                  fill: COLOR_ROJO,
                  fontSize: 10,
                  fontWeight: 600,
                }}
              />
            )}
            <Bar dataKey="absorcion_pct" name="% Absorción Comercial">
              {rows.map((r, i) => (
                <Cell key={i} fill={r.absorcion_pct >= benchmark ? COLOR_VERDE : COLOR_ROJO} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

// ---------------------- Heatmap columnas x pisos ----------------------

const _colorFor = (absorcion, benchmark) => {
  if (absorcion == null || benchmark == null) return { bg: '#475569', fg: '#94a3b8' };
  if (absorcion === 0) return { bg: COLOR_ROJO, fg: '#fff' };
  if (absorcion >= benchmark) return { bg: COLOR_VERDE, fg: '#fff' };
  if (absorcion >= benchmark * 0.6) return { bg: COLOR_NARANJA, fg: '#fff' };
  return { bg: COLOR_ROJO, fg: '#fff' };
};

export const HeatmapPisos = ({ columnas, rangos, celdas, benchmark }) => {
  const idx = new Map();
  celdas.forEach((c) => idx.set(`${c.rango}|${c.columna}`, c));
  const rangosDesc = [...rangos].reverse();

  return (
    <Card
      icon={<Grid3x3 size={14} />}
      title="Esquema Estructural y Mapa de Calor Dinámico por Pisos (rangos de 3)"
    >
      <p className="text-[11px] text-[#888888] mb-3">
        Las celdas cambian de color automáticamente según el porcentaje de absorción por columna física frente a la media del proyecto.
      </p>
      <div className="overflow-x-auto" data-testid="av-heatmap">
        <div className="inline-block min-w-full bg-slate-800 rounded-lg p-3">
          <div className="grid" style={{ gridTemplateColumns: `100px repeat(${columnas.length}, minmax(90px,1fr))` }}>
            <div className="text-center text-slate-400 text-[10px] py-2"></div>
            {columnas.map((col) => (
              <div
                key={col.columna}
                className="text-center text-slate-200 border-b border-slate-700 py-2 px-1"
                data-testid={`av-heatmap-col-${col.columna}`}
              >
                <p className="text-sm font-semibold">{col.columna}</p>
                <p className="text-[9px] text-slate-400 truncate">{col.tipologia_dominante || '—'}</p>
                <p className="text-[9px]" style={{ color: COLOR_VERDE }}>{col.area_promedio_m2}m²</p>
              </div>
            ))}
          </div>
          {rangosDesc.map((rango) => (
            <div
              key={rango}
              className="grid mt-1"
              style={{ gridTemplateColumns: `100px repeat(${columnas.length}, minmax(90px,1fr))` }}
              data-testid={`av-heatmap-row-${rango}`}
            >
              <div className="text-slate-300 text-[11px] font-semibold pr-2 flex items-center justify-end sticky left-0 bg-slate-800 z-10">
                PISOS {rango}
              </div>
              {columnas.map((col) => {
                const c = idx.get(`${rango}|${col.columna}`);
                const abs = c?.absorcion_pct;
                const total = c?.total_und || 0;
                const vend = c?.vendidas_und || 0;
                const hasData = total > 0;
                const style = hasData ? _colorFor(abs, benchmark) : { bg: '#475569', fg: '#94a3b8' };
                return (
                  <div
                    key={col.columna}
                    className="h-14 m-0.5 rounded flex flex-col items-center justify-center text-xs font-semibold"
                    style={{ background: style.bg, color: style.fg }}
                    title={hasData ? `${fmtPct(abs)} (${vend}/${total})` : 'Sin unidades'}
                    data-testid={`av-heatmap-cell-${rango}-${col.columna}`}
                  >
                    {hasData ? (
                      <>
                        <span>{Math.round((abs || 0) * 100)}%</span>
                        <span className="text-[9px] font-normal opacity-90">
                          ({vend}/{total})
                        </span>
                      </>
                    ) : (
                      <span>-</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-[#5A5A5A]">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded inline-block" style={{ background: COLOR_VERDE }}></span>
          Tracción Alta (&gt; Promedio)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded inline-block" style={{ background: COLOR_NARANJA }}></span>
          Estable (~ Promedio)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded inline-block" style={{ background: COLOR_ROJO }}></span>
          Baja (&lt; Promedio)
        </span>
      </div>
    </Card>
  );
};

// Ícono re-exportado para usar en el card de Filtros de index.js
export { Sliders as FiltrosIcon, Bot as ConsultorIcon, ClipboardList as DiagnosticoIcon };


// ============================================================
// Matriz 1: DETALLE RECAUDACIÓN
// ============================================================
export const MatrizRecaudacionCard = ({ payload }) => {
  const filas = payload?.filas || [];
  const columnas = payload?.columnas || [
    { key: 'V', label: 'Vendido' },
    { key: 'P', label: 'Proceso' },
    { key: 'S', label: 'Separado' },
    { key: 'D', label: 'Disponible' },
    { key: 'TOTAL', label: 'Total Rec. Proyec.' },
  ];
  return (
    <Card
      icon={<Wallet size={14} />}
      title="DETALLE RECAUDACIÓN"
      subtitle={
        <span className="inline-block bg-[#5a4a3a] text-[#fefaf3] rounded-md px-2 py-0.5">
          Al momento actual
        </span>
      }
    >
      <div className="overflow-x-auto" data-testid="av-matriz-recaudacion">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[#888888] text-[10px] uppercase tracking-wider bg-[#faf7f2]">
              <th className="text-left px-2 py-2 font-medium border-b border-[#e8dfd0]">Tipo</th>
              {columnas.map((c) => (
                <th key={c.key} className="text-right px-2 py-2 font-medium border-b border-[#e8dfd0]">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => {
              const isTotal = f.is_total === true;
              return (
                <tr
                  key={f.tipo}
                  className={`border-b border-[#f5f1ea] ${
                    isTotal ? 'bg-[#fdf8ee] font-semibold' : ''
                  }`}
                  data-testid={`av-recaudacion-row-${i}`}
                >
                  <td className="px-2 py-1.5 text-[#2D2D2D] whitespace-nowrap">
                    {f.tipo}
                  </td>
                  {columnas.map((c) => {
                    const monto = f.montos?.[c.key] ?? 0;
                    const pct = f.pcts?.[c.key];
                    const showPct = c.key !== 'TOTAL' && pct != null;
                    return (
                      <td key={c.key} className="px-2 py-1.5 text-right">
                        <div className="text-[#2D2D2D] leading-tight">
                          {monto > 0 ? fmtSoles(monto) : <span className="skin-text-mute">S/. —</span>}
                        </div>
                        {showPct && (
                          <div className="text-[10px] text-[#8a7a68] leading-tight">
                            {(pct * 100).toFixed(1)}%
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};


// ============================================================
// Matriz 2: EVALUACIONES Y PROYECCIONES
// ============================================================
export const MatrizEvaluacionesCard = ({ payload, esAdmin, onEditPanel }) => {
  const filas = payload?.filas || [];
  const alertaOk = payload?.alerta_ok;
  const margen = payload?.margen_maniobra;
  const panelAplica = payload?.panel_control?.aplica === true;
  const filtrosActivos = payload?.filtros_activos === true;

  const rowClass = (tipo) => {
    switch (tipo) {
      case 'subtotal':
        return 'bg-[#6b6b6b] text-white font-semibold';
      case 'total':
        return 'bg-[#3a3a3a] text-white font-bold';
      case 'manual':
        return 'bg-[#e0eefa] text-[#1a3d5c] font-semibold';
      default:
        return 'bg-white';
    }
  };

  return (
    <Card
      icon={<LineChart size={14} />}
      title="EVALUACIONES Y PROYECCIONES"
      subtitle={
        <span className="inline-block bg-[#5a4a3a] text-[#fefaf3] rounded-md px-2 py-0.5">
          Al momento actual
        </span>
      }
    >
      <div data-testid="av-matriz-evaluaciones" className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[#888888] text-[10px] uppercase tracking-wider bg-[#faf7f2]">
              <th className="text-left px-3 py-2 font-medium border-b border-[#e8dfd0]"></th>
              <th className="text-right px-3 py-2 font-medium border-b border-[#e8dfd0] whitespace-nowrap">
                {payload?.columna_header || 'Monto (S/.)'}
              </th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => (
              <tr
                key={`${f.label}-${i}`}
                className={`${rowClass(f.tipo)} border-b border-[#f5f1ea]`}
                data-testid={`av-eval-row-${i}`}
              >
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-2">
                    <span>{f.label}</span>
                    {f.tipo === 'manual' && esAdmin && (
                      <button
                        type="button"
                        onClick={onEditPanel}
                        className="text-[#1a3d5c] hover:text-[#0d2236] hover:bg-white/60 rounded p-0.5"
                        data-testid="av-panel-edit-btn"
                        title="Editar Panel de Control (solo Admin)"
                      >
                        <Edit2 size={12} />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  {f.valor === 0
                    ? <span className={f.tipo === 'detalle' ? 'skin-text-mute' : ''}>S/. —</span>
                    : fmtSoles(f.valor)}
                </td>
              </tr>
            ))}
            {panelAplica && alertaOk && (
              <tr className="border-b border-[#f5f1ea]" data-testid="av-eval-alerta-row">
                <td className="px-3 py-1.5">
                  <span
                    className="inline-block px-2 py-0.5 rounded font-semibold"
                    style={{
                      background: alertaOk.estado === 'OK' ? '#c8e6c9' : '#fff59d',
                      color: alertaOk.estado === 'OK' ? '#1b5e20' : '#b26a00',
                    }}
                  >
                    {alertaOk.estado}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap font-semibold"
                    style={{ color: alertaOk.valor >= 0 ? COLOR_VERDE : COLOR_ROJO }}>
                  {fmtSoles(alertaOk.valor)}
                </td>
              </tr>
            )}
            {panelAplica && margen !== null && margen !== undefined && (
              <tr data-testid="av-eval-margen-row">
                <td className="px-3 py-1.5 text-[#2D2D2D] italic">Margen de maniobra</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap font-semibold"
                    style={{ color: margen >= 0 ? COLOR_VERDE : COLOR_ROJO }}>
                  {fmtSoles(margen)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {filtrosActivos && (
          <div className="text-[11px] text-[#8a7a68] italic mt-2 px-1"
               data-testid="av-eval-panel-hidden-note">
            * Panel de Control, Alerta/OK y Margen de Maniobra se ocultan al
            aplicar filtros de segmentación (tipología o columna); esos
            indicadores se definen a nivel proyecto+etapa.
          </div>
        )}
      </div>
    </Card>
  );
};


// Modal de edición del Panel de Control (solo Admin)
export const PanelControlEditModal = ({
  open, onClose, proyecto, etapa, valorActual, onSaved,
}) => {
  const [valor, setValor] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setValor(valorActual != null ? String(valorActual) : '');
    }
  }, [open, valorActual]);

  if (!open) return null;

  const handleGuardar = async () => {
    const num = Number(valor);
    if (!isFinite(num) || num < 0) {
      alert('Ingresa un monto válido (>= 0).');
      return;
    }
    setSaving(true);
    try {
      await onSaved({ codigo_proyecto: proyecto, codigo_etapa: etapa, valor: num });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="av-panel-edit-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#3a3a3a]">Editar Panel de Control</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XIcon size={18} />
          </button>
        </div>
        <div className="space-y-3 text-sm">
          <div className="text-[#5a4a3a]">
            <div>Proyecto: <b>{proyecto}</b></div>
            <div>Etapa: <b>{etapa || '(sin etapa)'}</b></div>
          </div>
          <div>
            <label className="block text-[#5a4a3a] mb-1">
              Nuevo valor (S/.)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-full border border-[#c5b8a2] rounded px-2 py-1.5"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              data-testid="av-panel-edit-input"
              autoFocus
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded border border-[#c5b8a2] text-[#5a4a3a] hover:bg-[#f5f1ea]"
            data-testid="av-panel-edit-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            disabled={saving}
            className="px-3 py-1.5 rounded bg-[#5a4a3a] text-white hover:bg-[#3a3a3a] disabled:opacity-50"
            data-testid="av-panel-edit-save"
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

